import { useState, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon, AppsIcon, CampaignIcon, AdsIcon, PwaIcon, FunnelIcon, GenerateIcon,
  ReferralIcon, DevicesIcon, SettingsIcon, NotificationsIcon, TrackerIcon, UsersIcon, AuditIcon,
  ChecklistIcon, KeyIcon, ChartIcon, OffersIcon,
} from './icons';
import {
  BatchesIcon, BookIcon, FactoryIcon, BugIcon, CalendarIcon, CursorIcon, FacebookIcon, FirebaseIcon, GameIcon,
  GlobeIcon, InboxIcon, LinkIcon, MailIcon, PlayStoreIcon, PublishIcon, PulseIcon,
  PushTestIcon, RadarIcon, SetupIcon, TemplateIcon, WebhookIcon,
} from './icons/NavigationIcons';
import { useAuthStore } from '../store/auth';
import type { AdminSection } from '../api/types';
import { useBranding } from '@/hooks/useBranding';

type Icon = ComponentType<{ className?: string }>;

interface NavItem {
  icon?: Icon;
  label: string;
  path: string;
  section?: AdminSection;
  end?: boolean;
}

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

type GroupId = 'apps' | 'ads' | 'tracker' | 'push' | 'admin' | 'qa' | 'credentials';

const STORAGE_KEY = 'newads.sidebar.groups.v1';

const dashboardItem: NavItem = {
  icon: HomeIcon,
  label: 'Dashboard',
  path: '/',
  section: 'dashboard',
  end: true,
};

const appsItems: NavItem[] = [
  { icon: AppsIcon, label: 'Apps', path: '/apps', section: 'apps' },
  { icon: FactoryIcon, label: 'App Factory', path: '/apps-setup/factory', section: 'apps-setup' },
  { icon: SetupIcon, label: 'Apps Setup', path: '/apps-setup', section: 'apps-setup', end: true },
  { icon: BatchesIcon, label: 'Update Batches', path: '/apps-setup/update-batches', section: 'apps-setup' },
  { icon: GlobeIcon, label: 'ASO Intelligence', path: '/apps-setup/aso', section: 'apps-setup' },
  { icon: DevicesIcon, label: 'Devices', path: '/devices', section: 'devices' },
  { icon: FirebaseIcon, label: 'Firebase', path: '/firebase', section: 'firebase' },
  { icon: PlayStoreIcon, label: 'Play Console', path: '/play', section: 'play', end: true },
  { icon: PublishIcon, label: 'First Publication', path: '/play/publish', section: 'play' },
  { icon: PulseIcon, label: 'Android Vitals', path: '/play/vitals', section: 'play' },
  // Super admins only (filtered below): Google logins with their 2FA.
  { icon: GlobeIcon, label: 'Browser Profiles', path: '/browser-profiles', section: 'browser' },
  { icon: PushTestIcon, label: 'Push Test', path: '/push-test', section: 'push-test' },
];

const adsItems: NavItem[] = [
  { icon: CampaignIcon, label: 'Campaigns', path: '/campaigns', section: 'campaigns' },
  { icon: AdsIcon, label: 'Ads', path: '/ads', section: 'ads' },
  { icon: PwaIcon, label: 'PWA', path: '/pwa', section: 'pwa' },
  { icon: FunnelIcon, label: 'Funnel', path: '/funnel', section: 'funnel' },
  { icon: GenerateIcon, label: 'Generate', path: '/generate', section: 'generate' },
  { icon: ReferralIcon, label: 'Referrals', path: '/referrals', section: 'referrals' },
];

// These tools do not belong to the product groups requested above, so they
// remain directly accessible from the first level of the menu.
const standaloneItems: NavItem[] = [
  { icon: FunnelIcon, label: 'Performance', path: '/performance', section: 'perf' },
  { icon: KeyIcon, label: 'Public API', path: '/public-api', section: 'public-api' },
  { icon: RadarIcon, label: 'iGaming Radar', path: '/radar', section: 'radar' },
];

const pushItems: NavItem[] = [
  { icon: AppsIcon, label: 'Apps', path: '/push/apps' },
  { icon: TemplateIcon, label: 'Templates', path: '/push/templates' },
  { icon: CalendarIcon, label: 'Schedules', path: '/push/schedules' },
  { icon: ChartIcon, label: 'Analytics', path: '/push/analytics' },
];

const trackerItems: NavItem[] = [
  { icon: TrackerIcon, label: 'Overview', path: '/tracker', end: true },
  { icon: GlobeIcon, label: 'Domains', path: '/tracker/domains' },
  { icon: GlobeIcon, label: 'SEO Domains', path: '/tracker/seo-domains' },
  { icon: CampaignIcon, label: 'Campaigns', path: '/tracker/campaigns' },
  { icon: GlobeIcon, label: 'Geo Routers', path: '/tracker/geo-routers' },
  { icon: CursorIcon, label: 'Clicks', path: '/tracker/clicks' },
  { icon: FunnelIcon, label: 'Conversions', path: '/tracker/conversions' },
  { icon: WebhookIcon, label: 'Postback Logs', path: '/tracker/postback-logs' },
  { icon: OffersIcon, label: 'Offers', path: '/offers' },
  { icon: ChartIcon, label: 'Offer Performance', path: '/offers/performance' },
  { icon: PwaIcon, label: 'PWAs', path: '/tracker/pwas' },
  { icon: GameIcon, label: 'Playables', path: '/tracker/playables' },
  { icon: LinkIcon, label: 'Playable Links', path: '/tracker/playable-links' },
  { icon: PulseIcon, label: 'Playable Stats', path: '/tracker/playable-stats' },
  { icon: AuditIcon, label: 'Funnel Audits', path: '/funnel-audits', section: 'funnel-audits' },
  { icon: ChartIcon, label: 'Сверка', path: '/finance/reconciliation', section: 'finance' },
  { icon: PublishIcon, label: 'Публикаторы', path: '/finance/publishers', section: 'finance' },
];

const adminItems: NavItem[] = [
  // Requests intentionally has no AdminSection: every role has its own queue.
  { icon: InboxIcon, label: 'Requests', path: '/deletions' },
  { icon: MailIcon, label: 'Email', path: '/email', section: 'email' },
  { icon: NotificationsIcon, label: 'Notifications', path: '/notifications', section: 'notifications' },
  { icon: AuditIcon, label: 'Audit Log', path: '/audit', section: 'audit' },
  { icon: UsersIcon, label: 'Users', path: '/users', section: 'users' },
  { icon: SettingsIcon, label: 'Settings', path: '/settings', section: 'settings' },
];

const qaItems: NavItem[] = [
  { icon: BugIcon, label: 'QA', path: '/qa', section: 'qa' },
  { icon: BookIcon, label: 'Wiki', path: '/wiki', section: 'wiki' },
  { icon: ChecklistIcon, label: 'Checklist', path: '/checklist', section: 'checklist' },
];

// An outside client's whole menu: its apps, the listing/publishing workspace,
// and its account (the team, for the company's primary login).
const clientItems: NavItem[] = [
  { icon: AppsIcon, label: 'Apps', path: '/apps' },
  { icon: FactoryIcon, label: 'App Factory', path: '/apps-setup/factory' },
  { icon: SetupIcon, label: 'Apps Setup', path: '/apps-setup', end: true },
  { icon: UsersIcon, label: 'Account', path: '/account' },
  { icon: AuditIcon, label: 'Activity', path: '/activity' },
];

// A client's tracker: its own campaigns, offers, landings and their numbers.
const clientTrackerItems: NavItem[] = [
  { icon: TrackerIcon, label: 'Overview', path: '/tracker', end: true },
  { icon: CampaignIcon, label: 'Campaigns', path: '/tracker/campaigns' },
  { icon: GlobeIcon, label: 'Geo Routers', path: '/tracker/geo-routers' },
  { icon: OffersIcon, label: 'Offers', path: '/offers' },
  { icon: ChartIcon, label: 'Offer Performance', path: '/offers/performance' },
  { icon: PwaIcon, label: 'PWAs', path: '/tracker/pwas' },
  { icon: CursorIcon, label: 'Clicks', path: '/tracker/clicks' },
  { icon: FunnelIcon, label: 'Conversions', path: '/tracker/conversions' },
  { icon: WebhookIcon, label: 'Postback Logs', path: '/tracker/postback-logs' },
  { icon: GameIcon, label: 'Playables', path: '/tracker/playables' },
  { icon: LinkIcon, label: 'Playable Links', path: '/tracker/playable-links' },
  { icon: PulseIcon, label: 'Playable Stats', path: '/tracker/playable-stats' },
];

const credentialItems: NavItem[] = [
  { icon: KeyIcon, label: 'Credentials', path: '/credentials', section: 'credentials' },
  { icon: FacebookIcon, label: 'Facebook', path: '/facebook', section: 'facebook' },
];

const groupPaths: Record<GroupId, string[]> = {
  apps: appsItems.map((item) => item.path),
  ads: adsItems.map((item) => item.path),
  tracker: trackerItems.map((item) => item.path),
  push: pushItems.map((item) => item.path),
  admin: adminItems.map((item) => item.path),
  qa: qaItems.map((item) => item.path),
  credentials: credentialItems.map((item) => item.path),
};

function pathMatches(pathname: string, path: string) {
  return pathname === path || (path !== '/' && pathname.startsWith(`${path}/`));
}

function initialExpandedState(pathname: string): Record<GroupId, boolean> {
  let saved: Partial<Record<GroupId, boolean>> = {};

  try {
    const value = typeof window === 'undefined' ? null : window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = value ? JSON.parse(value) : null;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      saved = parsed as Partial<Record<GroupId, boolean>>;
    }
  } catch {
    // Storage can be disabled or contain stale data; the active group is a safe fallback.
  }

  return (Object.keys(groupPaths) as GroupId[]).reduce((state, id) => {
    state[id] = typeof saved[id] === 'boolean'
      ? saved[id]
      : groupPaths[id].some((path) => pathMatches(pathname, path));
    return state;
  }, {} as Record<GroupId, boolean>);
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.path}
      end={item.end}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition-colors duration-150 ${
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-content-muted hover:bg-surface-muted hover:text-content'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {item.icon ? (
            <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-content-subtle'}`} />
          ) : (
            <span className={`w-1.5 h-1.5 ml-1 mr-1 rounded-full flex-shrink-0 ${isActive ? 'bg-primary' : 'bg-border-strong'}`} />
          )}
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function TopLevelLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.path}
      end={item.end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
          isActive ? 'bg-primary/10 text-primary' : 'text-content-muted hover:bg-surface-muted hover:text-content'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {item.icon && <item.icon className={`w-5 h-5 ${isActive ? 'text-primary' : 'text-content-subtle'}`} />}
          {item.label}
        </>
      )}
    </NavLink>
  );
}

interface NavGroupProps {
  id: GroupId;
  icon: Icon;
  label: string;
  items: NavItem[];
  expanded: boolean;
  onToggle: () => void;
  pathname: string;
}

function NavGroup({ id, icon: GroupIcon, label, items, expanded, onToggle, pathname }: NavGroupProps) {
  const active = items.some((item) => pathMatches(pathname, item.path));
  const contentId = `sidebar-group-${id}`;

  return (
    <div className="rounded-lg">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={onToggle}
        className={`group flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 ${
          active
            ? 'bg-primary/10 text-primary'
            : 'text-content-muted hover:bg-surface-muted hover:text-content'
        }`}
      >
        <GroupIcon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-primary' : 'text-content-subtle group-hover:text-content-muted'}`} />
        <span className="flex-1 text-left">{label}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        id={contentId}
        aria-hidden={!expanded}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className={`overflow-hidden ${expanded ? 'visible' : 'invisible'}`}>
          <div className="ml-5 mt-1 border-l border-border pl-2 space-y-0.5">
            {items.map((item) => <NavItemLink key={item.path} item={item} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ open = false, onClose }: SidebarProps) {
  const { name: brand } = useBranding();
  const { pathname } = useLocation();
  const hasSection = useAuthStore((s) => s.hasSection);
  const admin = useAuthStore((s) => s.admin);
  const isSuper = !admin?.role || admin.role === 'super_admin';
  const isBdm = admin?.user_type === 'bdm';
  const isQa = useAuthStore((s) => s.isQa());
  const isClient = useAuthStore((s) => s.isClient());
  const showTracker = hasSection('tracker') || hasSection('offers') || hasSection('offers-performance')
    || hasSection('funnel-audits') || hasSection('finance');
  const showPush = hasSection('push');
  const visible = (items: NavItem[]) => items.filter((item) => !item.section || hasSection(item.section));

  const visibleApps = visible(appsItems);
  const visibleAds = visible(adsItems);
  const visibleStandalone = visible(standaloneItems);
  const visibleAdmin = visible(adminItems);
  const visibleQa = visible(qaItems);
  const visibleCredentials = visible(credentialItems);
  const visibleTracker = trackerItems.filter((item) => {
    if (isBdm && ![
      '/tracker', '/tracker/campaigns', '/tracker/clicks',
      '/tracker/conversions', '/tracker/domains', '/offers', '/offers/performance', '/funnel-audits',
      '/finance/reconciliation',
    ].includes(item.path)) return false;
    if (isQa && item.path === '/tracker') return false;
    // Reconciliation is its own grant: it is never implied by the tracker
    // section, and it is never narrowed to someone's own campaigns.
    if (item.path === '/finance/reconciliation') return hasSection('finance');
    if (item.path === '/offers') return hasSection('offers');
    if (item.path === '/offers/performance') return hasSection('offers-performance');
    if (item.path === '/tracker/domains') return hasSection('domains');
    // Buying domains spends money: super admin only.
    if (item.path === '/tracker/seo-domains') return isSuper;
    if (item.path === '/funnel-audits') return hasSection('funnel-audits');
    if (!hasSection('tracker')) return false;
    if (item.path === '/tracker/postback-logs') return isSuper;
    return true;
  });

  const [expanded, setExpanded] = useState<Record<GroupId, boolean>>(() => initialExpandedState(pathname));

  function toggleGroup(id: GroupId) {
    setExpanded((current) => {
      const next = { ...current, [id]: !current[id] };
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        }
      } catch {
        // The menu still works when storage is unavailable.
      }
      return next;
    });
  }

  const group = (id: GroupId, label: string, icon: Icon, items: NavItem[]) => (
    <NavGroup
      key={id}
      id={id}
      label={label}
      icon={icon}
      items={items}
      expanded={expanded[id]}
      onToggle={() => toggleGroup(id)}
      pathname={pathname}
    />
  );

  return (
    <aside
      className={`w-60 bg-surface border-r border-border flex flex-col h-screen fixed left-0 top-0 z-40
        transition-transform duration-200 ease-out
        ${open ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}
    >
      <div className="h-16 flex items-center justify-between pl-6 pr-3 border-b border-border flex-shrink-0">
        <span className="text-xl font-semibold text-content">
          <span className="text-primary">{brand}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="md:hidden p-2 -mr-1 text-content-subtle hover:text-content hover:bg-surface-muted rounded-md"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {isClient ? (
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {clientItems.slice(0, 3).map((item) => <TopLevelLink key={item.path} item={item} />)}
          {hasSection('tracker') && group('tracker', 'Tracker', TrackerIcon, clientTrackerItems)}
          {hasSection('radar') && <TopLevelLink item={{ icon: RadarIcon, label: 'iGaming Radar', path: '/radar' }} />}
          <TopLevelLink item={clientItems[3]!} />
          <TopLevelLink item={clientItems[4]!} />
        </nav>
      ) : (
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {hasSection(dashboardItem.section!) && <TopLevelLink item={dashboardItem} />}

        {visibleApps.length > 0 && group('apps', 'Apps', AppsIcon, visibleApps)}
        {visibleAds.length > 0 && group('ads', 'Ads', AdsIcon, visibleAds)}

        {visibleStandalone.length > 0 && (
          <div className="py-1 space-y-1">
            {visibleStandalone.map((item) => <TopLevelLink key={item.path} item={item} />)}
          </div>
        )}

        {showTracker && visibleTracker.length > 0 && group('tracker', 'Tracker', TrackerIcon, visibleTracker)}
        {showPush && group('push', 'Push', NotificationsIcon, pushItems)}

        <div className="pt-2 mt-2 border-t border-border space-y-1">
          {visibleAdmin.length > 0 && group('admin', 'Admin', SettingsIcon, visibleAdmin)}
          {visibleQa.length > 0 && group('qa', 'QA', ChecklistIcon, visibleQa)}
          {visibleCredentials.length > 0 && group('credentials', 'Credentials', KeyIcon, visibleCredentials)}
        </div>
      </nav>
      )}
    </aside>
  );
}
